import { Button } from "@/components/ui/button";
import { cn } from "@/components/utils/utils";
import { TravelRequest } from "@/lib/request-trip/models/travel-request.model";
import { Plus } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface TravelRequestButtonProps {
  travelRequest: TravelRequest;
  userRole: "designer" | "admin" | "user";
  onClick: (requestId: string) => void;
  onCreateItinerary?: (requestId: string) => void;
  variant?: "card" | "table";
  className?: string;
}

export const TravelRequestButton = ({
  travelRequest,
  userRole,
  onClick,
  onCreateItinerary,
  variant = "card",
  className
}: TravelRequestButtonProps) => {
  const { t } = useLanguage();
  
  const handleClick = () => {
    if (!isAdmin && isAssigned && onCreateItinerary) {
      onCreateItinerary(travelRequest.id);
    } else {
      onClick(travelRequest.id);
    }
  };

  const isAssigned = !!travelRequest.assigned_designer_id && travelRequest.status === "assigned";
  const isAdmin = userRole === "admin";
  const buttonText = isAssigned ? t('itinerary.createItinerary') : t('travelRequest.acceptOrDecline');
  const buttonVariant = isAssigned ? "default" : (travelRequest.isPending ? "secondary" : "outline");
  const isCompleted = travelRequest.status === "completed";

  // Se l'utente è un "admin" deve vedere il pulsante per visualizzare i dettagli
  // TODO: qua l'admin deve poter assegnare la richiesta al TD
  if (isAdmin) {
    if (variant === "card") {
      return (
        <Button
          variant="secondary"
          className={cn("w-full h-12 rounded-xl", className)}
          onClick={handleClick}
        >
          {t('travelRequest.viewRequest')}
        </Button>
      );
    }

    return (
      <Button
        variant="secondary"
        size="sm"
        onClick={handleClick}
        className={cn("text-xs", className)}
      >
        {t('travelRequest.viewRequest')}
      </Button>
    );
  }

  if (isCompleted) {
    return (
      <Button
        variant={"secondary"}
        className={cn("w-full h-12 rounded-xl", className)}
        onClick={handleClick}
      >
        {t('common.view')} {t('travelRequest.requestDetails')}
      </Button>
    );
  }

  if (variant === "card") {
    return (
      <Button
        variant={isAssigned ? "default" : "secondary"}
        className={cn("w-full h-12 rounded-xl", className)}
        onClick={handleClick}
      >
        {isAssigned && <Plus className="h-4 w-4 mr-2" />}
        {buttonText}
      </Button>
    );
  }

  // Table variant
  return (
    <Button
      variant={buttonVariant}
      size="sm"
      onClick={handleClick}
      className={cn("text-xs", className)}
    >
      {isAssigned && <Plus className="h-3 w-3 mr-1" />}
      {buttonText}
    </Button>
  );
};